import type { Messages } from './sv';

// English texts. Same keys as sv.ts.

export const en: Messages = {
  common: {
    openFullPage: 'Open full page',
    pickItem: 'Pick one in the list to see it here',
    demo: 'Demo data',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    all: 'All',
    viewAll: 'View all',
    loading: 'Loading',
    errorTitle: 'Could not load this',
    errorMessage: 'Something went wrong. Check your connection and try again.',
    retry: 'Try again',
    notFoundTitle: 'Page not found',
    notFoundText: 'The link may be old, or the page has moved.',
    goHome: 'Go to the start page',
    showingCount: 'Showing {shown} of {total}',
    showingRange: 'Showing {from}–{to} of {total}',
    previous: 'Previous',
    next: 'Next',
    clearFilters: 'Clear filters',
    noMatchesTitle: 'No matches',
    yes: 'Yes',
    no: 'No',
    noMatchesText: 'Try another search or clear the filters.',
  },
  layout: {
    nav: 'Main',
    menu: 'Menu',
    openMenu: 'Open menu',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    contextPanel: 'Details',
    collapsePanel: 'Hide details panel',
    expandPanel: 'Show details panel',
    helpTitle: 'Need a little help?',
    helpText: 'Our support team is always happy to assist you.',
    contactSupport: 'Contact support',
    search: 'Search',
    searchPlaceholder: 'Search …',
    closeSearch: 'Close search',
    notificationsUnread: 'Notifications, {count} unread',
    language: 'Language',
    account: 'Account menu',
    settings: 'Settings',
    logOut: 'Log out',
  },
  areas: {
    client: 'Client',
    provider: 'Provider',
    admin: 'Admin',
  },
  nav: {
    billing: 'Revenue',
    overview: 'Overview',
    myJobs: 'My jobs',
    messages: 'Messages',
    settings: 'Settings',
    findJobs: 'Find jobs',
    myProposals: 'My proposals',
    subscription: 'Subscription',
    companyProfile: 'Company profile',
    review: 'Review jobs',
    providers: 'Providers',
    users: 'Users',
    reports: 'Reports',
    system: 'System',
    auditLog: 'Audit log',
  },
  auth: {
    panel: {
      title: 'Every job in the home, simply done',
      text: 'Post the job, get proposals from checked tradespeople and choose the one that suits you.',
      points: [
        'Every company has a checked organisation number and F-skatt',
        'Free for clients, with no commitment',
        'Your contact details are shared only when you accept a proposal',
      ],
      footnote: '© Hemmafixa North AB',
    },
    showcase: {
      company: 'Måleri Väst AB',
      label: 'Paint kitchen walls',
      price: '18 500 kr',
      status: 'Accepted',
      from: 'Erik, Måleri Väst',
      message: 'Hi! I can come by on Thursday to look at the walls.',
      progress: '3 proposals in',
      caption: 'Checked companies near you',
    },
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    loginTitle: 'Log in',
    loginDescription: 'Welcome back to Hemmafixa.',
    login: 'Log in',
    forgotLink: 'Forgot your password?',
    providerLink: 'Are you a tradesperson? Sign up your company',
    noAccount: 'No account? You get one when you post your first job on hemmafixa.se.',
    wrongCredentials: 'Wrong email or password.',
    locked: 'Too many failed attempts. Wait a while or reset your password.',
    suspended: 'This account is suspended. Contact Hemmafixa if you think this is a mistake.',
    sessionEnded: 'You have been logged out. Please log in again.',
    emailInvalid: 'Enter a valid email address, e.g. anna@example.se.',
    passwordRequired: 'Enter your password.',
    forgotTitle: 'Forgot your password?',
    forgotDescription: 'Enter your email and we will send a link where you can choose a new password.',
    sendLink: 'Send link',
    sentTitle: 'Check your email',
    sentText:
      "If there is an account for {email}, we have sent a link. It works for one hour. If you can't find it, look in your spam folder.",
    backToLogin: 'Back to login',
    setTitle: 'Choose a password',
    resetTitle: 'Choose a new password',
    forEmail: 'For {email}',
    newPassword: 'New password',
    newPasswordHint: 'At least 10 characters. A sentence you remember works well.',
    repeatPassword: 'Repeat the password',
    passwordTooShort: 'Use at least 10 characters.',
    passwordTooLong: 'Use at most 200 characters.',
    passwordsDontMatch: 'The passwords are not the same.',
    savePassword: 'Save password',
    checkingLink: 'Checking the link',
    linkInvalidTitle: 'This link no longer works',
    linkInvalidText:
      'Links work once and only for a limited time. Ask for a new link and we will send it right away.',
    requestNewLink: 'Ask for a new link',
    passwordSaved: 'Your password is saved. You are logged in.',
    genericError: 'Something went wrong. Please try again in a moment.',
  },
  status: {
    job: {
      draft: 'Draft',
      in_review: 'In review',
      needs_info: 'Needs info',
      open: 'Open',
      provider_chosen: 'Provider chosen',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      expired: 'Expired',
    },
    proposal: {
      submitted: 'Sent',
      accepted: 'Accepted',
      declined: 'Declined',
      withdrawn: 'Withdrawn',
      chosen: 'Chosen',
      closed: 'Closed',
    },
    provider: {
      onboarding: 'Started',
      pending: 'Waiting',
      verified: 'Verified',
      rejected: 'Rejected',
    },
    user: {
      active: 'Active',
      suspended: 'Suspended',
    },
    subscription: {
      incomplete: 'Not finished',
      trialing: 'Trial',
      active: 'Active',
      past_due: 'Payment failed',
      canceled: 'Ended',
    },
    invoice: {
      draft: 'Draft',
      open: 'Unpaid',
      paid: 'Paid',
      uncollectible: 'Not paid',
      void: 'Void',
    },
  },
  categories: {
    malare: 'Painter',
    rormokare: 'Plumber',
    elektriker: 'Electrician',
    stad: 'Cleaning',
    snickare: 'Carpenter',
    tradgard: 'Garden',
  },
  wantedStart: {
    asap: 'As soon as possible',
    within_month: 'Within a month',
    flexible: 'Flexible',
  },
  job: {
    description: 'Description',
    questions: "Answers to the assistant's questions",
    noQuestions: 'No follow-up questions were answered.',
    contactTitle: 'Contact details',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    contactName: 'Contact person',
    details: 'Details',
    category: 'Category',
    mapLabel: 'Map of where the job is, {place}',
    mapField: 'Where on the map',
    mapHint: 'Click the map where the job is, then drag the pin to adjust.',
    useMyLocation: 'Use my location',
    clearPin: 'Remove pin',
    mapUnavailable:
      'The map is not available right now. The postal code is enough for companies to find the job.',
    location: 'Location',
    wantedStart: 'Wanted start',
    posted: 'Posted',
    client: 'Client',
    notAnswered: 'Not answered',
    fromAssistant: 'Assistant',
    fromAdmin: 'Hemmafixa',
  },
  providerSignup: {
    title: 'Take jobs through Hemmafixa',
    description: 'Sign up, tell us about your company and get jobs in your area.',
    name: 'Your name',
    phone: 'Mobile number',
    acceptTerms: 'I accept the terms for providers',
    submit: 'Sign me up',
    sentTitle: 'Check your email',
    sentText: 'We have sent the next step to {email}. The link works for 24 hours.',
    signedInTitle: 'Continue as a provider',
    signedInText: 'You are logged in as {name}. We will add a company to your account.',
    continue: 'Continue',
    notYou: 'Not you? Log out',
    alreadyProvider: 'You are already a provider.',
    toProvider: 'To your company profile',
    nameInvalid: 'Write your name',
    phoneInvalid: 'Enter a valid phone number, e.g. 070-123 45 67',
    termsRequired: 'Accept the terms to sign up',
    failed: 'Could not sign up. Try again in a moment.',
  },
  jobEdit: {
    title: 'Edit the job',
    fieldTitle: 'Title',
    fieldDescription: 'Description',
    fieldPostalCode: 'Postal code',
    invalidField: 'Check this field',
    unknownCategory: 'Choose a category from the list',
    unknownPostalCode: 'We could not find this postal code',
    savedToast: 'The changes are saved.',
    conflict: 'The job has changed. Reload the page and try again.',
    saveFailed: 'Could not save. Try again in a moment.',
  },
  client: {
    postJob: 'Post a job',
    proposalCount: '{count} proposals',
    overview: {
      title: 'Hi {name}',
      description: 'Your jobs and new proposals.',
      activeJobs: 'Active jobs',
      newProposals: 'Proposals to answer',
      unreadMessages: 'Unread messages',
      needsAnswers: 'Jobs waiting for your answers',
      chosenJobs: 'Jobs given out',
    },
    jobs: {
      title: 'My jobs',
      description: 'Every job you have posted.',
      searchPlaceholder: 'Search your jobs',
      columns: {
        job: 'Job',
        category: 'Category',
        status: 'Status',
        proposals: 'Proposals',
        posted: 'Posted',
      },
      filters: {
        all: 'All',
        active: 'Active',
        closed: 'Closed',
      },
      empty: 'No jobs yet',
      emptyText: 'Jobs you post on hemmafixa.se show up here.',
      needsAnswer: 'Answer the question',
    },
    job: {
      closeJob: 'Close the job',
      proposalsTitle: 'Proposals ({count})',
      noProposals: 'No proposals yet',
      noProposalsText: 'Providers in your area get the job as soon as it is approved.',
      proposalsText:
        'Compare the proposals. Accept one to see their contact details and chat; then give the job to the one you want.',
      fixedPrice: 'Fixed price',
      priceRange: 'Price range',
      startLabel: 'Start',
      durationLabel: 'Time',
      includes: 'Included',
      viewProfile: 'View company',
      sentAt: 'Sent {date}',
      contactHidden: 'Contact details show once you accept the proposal.',
      declineTitle: 'Decline the proposal from {company}?',
      declineText: 'The provider is told you declined. This cannot be undone.',
      declineReason: 'Reason (optional)',
      declineReasonHint: 'Shown to the provider, for example “Too expensive” or “Cannot start in time”.',
      declineFailed: 'Could not decline. Reload the page and try again.',
      yourReason: 'Your reason',
      otherProposals: 'Declined and closed ({count})',
      declinedToast: 'Proposal declined.',
      acceptTitle: 'Accept the proposal from {company}?',
      acceptText:
        'You will see each other’s contact details and a conversation opens. You can accept up to {max} proposals on a job and still decide later.',
      acceptedToast: 'Proposal accepted. You can now contact the company.',
      acceptFailed:
        'Could not accept. The job may have closed, or you have already accepted {max} proposals.',
      closeHow: 'How do you want to close it?',
      chooseProvider: 'Give the job to {company}',
      cancelJob: 'Cancel the job',
      cancelOnlyText: 'To give the job to a company, accept their proposal first.',
      cancelReason: 'Why are you cancelling? (optional)',
      cancelReasonHint: 'Only Hemmafixa sees this.',
      chosenToast: 'Done! The company is told, and your conversation stays open.',
      cancelledToast: 'The job is cancelled.',
      closeFailed: 'Could not close the job. Reload the page and try again.',
      closeTitle: 'Close the job?',
      closeText: 'The job leaves the job list and the providers are told. This cannot be undone.',
      closedToast: 'The job is closed.',
      verified: 'Verified provider',
      startFrom: 'Can start {date}',
      duration: 'About {days} days',
      accept: 'Accept',
      decline: 'Decline',
      edit: 'Edit the job',
      editText: 'You can change the job until it is approved.',
      inReviewText: 'Hemmafixa is reviewing the job. Providers see it once it is approved.',
      rejectedTitle: 'The job was not approved',
      questionsTitle: 'Hemmafixa has a question',
      questionsText: 'Answer it and we will review the job again.',
      yourAnswer: 'Your answer',
      sendAnswers: 'Send answers',
      answerRequired: 'Write an answer',
      answeredToast: 'Thank you! We will review the job again.',
      answerFailed: 'Could not send the answer. Try again in a moment.',
      questions: 'Questions and answers',
    },
  },
  provider: {
    overview: {
      title: 'Hi {name}',
      description: 'Where your company stands and what to do next.',
      newJobs: 'New matching jobs',
      waiting: 'Proposals waiting for an answer',
      accepted: 'Accepted: talk to the client',
      savedJobs: 'Saved jobs',
      won: 'Jobs won, last 30 days',
      activeProposals: 'Active proposals',
      acceptedCount: '{count} accepted',
      unreadMessages: 'Unread messages',
      credits: 'Credits left',
      renews: 'Renews {date}',
      latestJobs: 'Latest jobs',
      viewProfile: 'View company profile',
      onboardingTitle: 'Finish your company profile',
      onboardingText: 'Fill in the company, what you do and where you work, and we will verify you.',
      onboardingAction: 'Continue',
    },
    findJobs: {
      title: 'Find jobs',
      description: 'Approved jobs in your categories and areas, waiting for proposals.',
      searchPlaceholder: 'Search title or description',
      allCategories: 'All categories',
      empty: 'No open jobs right now',
      emptyText: 'New jobs show up here once Hemmafixa has approved them.',
      quoted: 'Proposal sent',
      notVerifiedTitle: 'Your company is not verified yet',
      notVerifiedText: 'Once we have verified the company you can see jobs and send proposals.',
      noPlanTitle: 'Choose a plan to see jobs',
      noPlanText: 'With an active plan you get jobs in your categories and areas and can send proposals.',
      unpaidTitle: 'Your subscription is unpaid',
      unpaidText: 'Update your card to see jobs and send proposals again. Your conversations carry on.',
      toBilling: 'Go to subscription',
      toProfile: 'Go to company profile',
      show: 'Show',
      allJobs: 'All jobs',
      savedJobs: 'Saved',
      anyStart: 'Any start',
      yourProposal: 'Your proposal',
      notQuoted: 'No proposal yet',
      savedEmpty: 'No saved jobs',
      savedEmptyText: 'Save a job with the bookmark and you will find it here.',
      details: 'Job details',
      pickJob: 'Pick a job in the list',
      openJob: 'Open job',
      sendProposal: 'Send proposal',
      proposals: 'Proposals so far',
      save: 'Save',
      unsave: 'Remove from saved',
      savedToast: 'Job saved',
      unsavedToast: 'Removed from saved',
    },
    job: {
      yourProposal: 'Your proposal',
      closedText: 'The job is no longer open.',
      priceType: 'Price',
      fixedPrice: 'Fixed price',
      priceRange: 'Price range',
      price: 'Price (SEK)',
      priceFrom: 'Price from (SEK)',
      priceTo: 'Price to (SEK)',
      priceHint: 'What the client pays, including VAT.',
      includes: 'What is included',
      includesHint: 'For example material, waste removal and travel.',
      start: 'Earliest start',
      duration: 'Number of days',
      message: 'Message to the client',
      messageHint:
        'At least 20 characters. Contact details are hidden until the client accepts the proposal.',
      send: 'Send proposal',
      saveChanges: 'Save changes',
      sentToast: 'The proposal is sent.',
      savedToast: 'The proposal is changed.',
      edit: 'Change proposal',
      withdraw: 'Withdraw',
      withdrawTitle: 'Withdraw the proposal?',
      withdrawText: 'The client no longer sees it, and you cannot send a new proposal on this job.',
      withdrawnToast: 'The proposal is withdrawn.',
      sentAt: 'Sent {date}',
      declineReason: 'The client’s reason',
      contactHidden:
        'The client does not see phone numbers and email addresses in the text until they accept.',
      waitingText: 'The client has your proposal. You can change it until they answer.',
      failed: 'Could not send. Try again in a moment.',
      conflict: 'The proposal or the job has changed. Reload the page.',
      sendProposal: 'Send proposal',
      viewProposal: 'Your proposal',
      proposals: 'Proposals',
      proposalsSoFar: '{count} proposals so far',
      errors: {
        priceFromOre: 'Enter a price in whole kronor, at least 1 kr.',
        priceToOre: 'Must be higher than the lowest price.',
        earliestStart: 'Choose a valid date.',
        durationDays: 'Enter 1–365 days.',
        message: 'Write at least 20 characters.',
        includes: 'At most 1,000 characters.',
      },
    },
    proposals: {
      title: 'My proposals',
      description: 'Every proposal you have sent.',
      show: 'Show',
      filters: { all: 'All', active: 'Ongoing', closed: 'Finished' },
      columns: {
        job: 'Job',
        price: 'Price',
        status: 'Status',
        sent: 'Sent',
      },
      empty: 'No proposals here',
      emptyText: 'Find a job and send your first proposal.',
      findJobs: 'Find jobs',
    },
    onboarding: {
      title: 'Company profile',
      description: 'Fill in your details so we can verify your company.',
      descriptionVerified: 'What clients see about your company.',
      progress: 'Step {current} of {total}',
      steps: {
        company: 'Company',
        services: 'Services and areas',
        documents: 'Documents',
        review: 'Review and send',
      },
      stepText: [
        'Shown on your public profile.',
        'You get jobs in the categories and counties you choose.',
        'We need your company registration and F-skatt certificate before your first proposal.',
        'Check the details before you send them.',
      ],
      companyName: 'Company name',
      orgNumber: 'Organisation number',
      orgNumberHint: '10 digits, for example 556123-4567. Sole trader: your personal identity number.',
      about: 'About the company',
      aboutHint: 'What you do and what you are good at. Shown to clients.',
      website: 'Website',
      fSkatt: 'The company has F-skatt',
      fSkattHint: 'Required to take jobs through Hemmafixa.',
      selectAll: 'Select all',
      clearAll: 'Clear all',
      showServices: 'Show services in {name}',
      selectedCount: '{count}/{total}',
      categories: 'Categories',
      counties: 'Counties',
      documentsTitle: 'Documents',
      documentsText:
        'Upload your company registration and F-skatt certificate to speed up the check. You can also send them later.',
      documentsVerifiedText:
        'Replace a document when it changes, for example a new registration certificate.',
      docTypes: {
        company_registration: 'Company registration',
        f_skatt: 'F-skatt certificate',
        other: 'Other documents',
      },
      docTypeHints: {
        f_skatt: 'Proof from the Swedish Tax Agency that the company has F-skatt.',
        company_registration:
          'Registration certificate from Bolagsverket (registreringsbevis), not older than 3 months.',
        other: 'For example qualifications, certificates or authorisations.',
      },
      docDrop: 'Drag your {document} here',
      docHint: 'PDF, JPG or PNG, up to 10 MB.',
      submit: 'Send for review',
      doneToast: 'Sent for review. We will tell you by email.',
      savedToast: 'Saved.',
      saveFailed: 'Could not save. Try again in a moment.',
      invalidField: 'Check this field',
      orgNumberInvalid: 'Check the organisation number; it does not add up',
      orgNumberTaken: 'Another account already uses this organisation number',
      chooseCategory: 'Choose at least one category',
      chooseCounty: 'Choose at least one county',
      edit: 'Change',
      notSet: 'Not filled in',
      pendingTitle: 'We are reviewing your company',
      pendingText:
        'We check the organisation number and F-skatt, usually within one working day. You will hear from us by email.',
      verifiedTitle: 'Your company is verified',
      verifiedText: 'You can choose a plan and start sending proposals.',
      verifiedEditText:
        'You can change the description, website, categories and counties here. We have checked the company name, organisation number and F-skatt; to change them, email hej@hemmafixa.se.',
      checkedDetails: 'Checked details',
      publicDetails: 'Shown to clients',
      viewPublic: 'View your public profile',
      saveDetails: 'Save the description',
      saveServices: 'Save services and areas',
      servicesHint: 'You get jobs in the categories and counties you choose.',
      websiteInvalid: 'Enter a web address, e.g. www.example.se',
      rejectedTitle: 'We need more details',
      rejectedText: 'Correct the details below and send again.',
    },
    subscription: {
      title: 'Subscription',
      description: 'Your plan, credits for proposals and invoices.',
      creditsLeft: 'Credits left this period',
      creditsHint: 'One credit per proposal. Unused plan credits reset each period; extra packs stay.',
      buyPack: 'Buy {credits} credits for {price}',
      packSuccess: 'Thank you! The credits are added as soon as Stripe confirms the payment.',
      packCancel: 'Purchase cancelled. Nothing was charged.',
      renews: 'Next renewal',
      trialEnds: 'Trial ends',
      endsOn: 'Ends',
      current: 'Current',
      choose: 'Choose',
      startTrial: 'Try free for {days} days',
      perMonth: '/ month',
      proposalsPerMonth: '{count} proposals a month',
      allCategories: 'All categories and all of Sweden',
      vatNote: 'Prices exclude VAT. Payments go through Stripe.',
      plansTitle: 'Choose a plan',
      currentTitle: 'Your plan',
      noSubscription: 'You have no plan yet. Choose one to send proposals.',
      manage: 'Manage subscription',
      manageHint: 'Change plan or card, or cancel, at Stripe.',
      changeInPortal: 'Change under Manage subscription',
      pastDue: 'Your payment failed. Update your card before {date} to keep sending proposals.',
      canceling: 'Your subscription ends on {date}.',
      billingOff: 'Payments are not set up yet. You can see the plans but not choose one.',
      checkoutSuccess: 'Thank you! Your plan starts as soon as Stripe confirms the payment.',
      checkoutCancel: 'Payment cancelled. Nothing was charged.',
      invoicesTitle: 'Invoices',
      noInvoices: 'No invoices yet.',
      invoiceNumber: 'Number',
      invoicePeriod: 'Period',
      invoiceTotal: 'Amount incl. VAT',
      invoiceStatus: 'Status',
      viewInvoice: 'View',
      downloadPdf: 'PDF',
    },
  },
  charts: {
    lastSixMonths: 'Last 6 months',
    proposalsSentWon: 'Proposals sent and won',
    sent: 'Sent',
    won: 'Won',
    yourProposals: 'Your proposals',
    proposals: 'proposals',
    jobsAndProposals: 'Your jobs and proposals',
    jobs: 'Jobs posted',
    proposalsReceived: 'Proposals received',
    yourJobs: 'Your jobs',
    jobsCenter: 'jobs',
    platformActivity: 'Platform activity',
    newJobs: 'New jobs',
    newUsers: 'New users',
    newProposals: 'Proposals',
    providersByStatus: 'Companies',
    companies: 'companies',
    other: 'Other',
    inReview: 'In review',
    aiBudget: 'AI budget this month',
    winRate: 'Win rate',
    winRateText: '{won} of {total} proposals won',
    active: 'Active',
    unread: 'Unread',
    open: 'Open',
    thisMonth: 'this month',
  },
  dashboard: {
    postJobTitle: 'Need more help at home?',
    postJobText: 'Post a new job and get proposals from verified companies, usually within a day.',
    myJobs: 'My jobs',
    reviewTitle: 'Jobs waiting for review',
    reviewText: 'Approve, ask the client or reject. Every job is checked before companies see it.',
    openQueue: 'Open the queue',
  },
  overview: {
    needsAttention: 'Needs your attention',
    nothingWaiting: 'Nothing is waiting for you right now.',
    recentActivity: 'Recent activity',
    noActivity: 'Nothing has happened yet.',
    compareProposals: 'New proposals on “{title}”: {count}',
    compareProposalsText: 'Compare price and start, then accept the ones you want to talk to.',
    compare: 'Compare',
    chooseProvider: 'Agreed on “{title}”? Give the job to the company',
    chooseProviderText: 'Accepted: {names}. Choose one under Close the job when you have agreed.',
    choose: 'Choose',
    answerQuestions: 'Hemmafixa has questions about “{title}”',
    answerQuestionsText: 'Answer them so the job can be published.',
    answer: 'Answer',
    unreadMessages: 'Unread messages from {name}: {count}',
    openChat: 'Open chat',
    talkToClient: '{client} accepted your proposal on “{title}”',
    talkToClientText: 'Agree on the details; the client gives you the job when you are done.',
    newJobs: 'New jobs that match your company: {count}',
    newJobsText: 'Jobs in your categories and areas you have not quoted on.',
    findJobs: 'Find jobs',
    reviewJobs: 'Jobs waiting for review: {count}',
    verifyProviders: 'Companies waiting for verification: {count}',
    handleReports: 'Open reports: {count}',
    review: 'Review',
    handle: 'Handle',
  },
  report: {
    button: 'Report',
    title: {
      message: 'Report the message',
      proposal: 'Report the proposal',
      provider: 'Report the company',
      user: 'Report the person',
    },
    text: 'Hemmafixa looks at every report. The other side is not told who reported.',
    reasonLabel: 'What is wrong?',
    reasonRequired: 'Choose a reason',
    reasons: {
      spam: 'Advertising or spam',
      abusive: 'Rude or abusive',
      fraud: 'Looks like fraud',
      off_platform: 'Wants to move contact or payment outside Hemmafixa',
      other: 'Something else',
    },
    details: 'More details (optional)',
    detailsHint: 'What happened? Only Hemmafixa reads this.',
    send: 'Send report',
    sentToast: 'Thank you. Hemmafixa will look at it.',
    alreadyReported: 'You have already reported this.',
    failed: 'Could not send the report. Try again in a moment.',
  },
  publicProfile: {
    title: 'Provider profile',
    preview: 'This is how clients see your profile.',
    verified: 'Verified company',
    verifiedHint: 'Hemmafixa has checked the organisation number and F-skatt.',
    orgNumber: 'Org number',
    memberSince: 'Member since {date}',
    about: 'About the company',
    noAbout: 'The company has not written a description yet.',
    services: 'Services',
    areas: 'Works in',
    website: 'Website',
    contactNote: 'Contact details are shared once you accept a proposal from the company.',
  },
  admin: {
    billing: {
      title: 'Subscriptions and revenue',
      description: 'Subscriptions, monthly revenue, churn and plans.',
      tabs: { overview: 'Overview', subscriptions: 'Subscriptions', plans: 'Plans' },
      mrr: 'Monthly revenue (MRR)',
      mrrHint: 'Active and past due, excl. VAT',
      active: 'Active',
      trialing: 'Trial',
      pastDue: 'Payment failed',
      churnHint: 'Churn this month: {rate}',
      revenue: 'Revenue per month',
      revenueHint: 'Paid invoices excl. VAT, last 12 months',
      startedChurned: 'New and ended subscriptions',
      started: 'New',
      churned: 'Ended',
      searchPlaceholder: 'Search company, org number or email',
      allStatuses: 'All',
      empty: 'No subscriptions yet.',
      columns: {
        company: 'Company',
        plan: 'Plan',
        price: 'Price / month',
        status: 'Status',
        renews: 'Next date',
        started: 'Created',
      },
      endsOn: 'Ends {date}',
      trialUntil: 'Trial until {date}',
      graceUntil: 'Pay before {date}',
      noCompany: 'No company name',
      subscribers: '{count} subscribers',
      inactive: 'Hidden from new',
      notInStripe: 'Created in Stripe at the first purchase',
      edit: 'Edit',
      editTitle: 'Edit {name}',
      priceNote: 'A new price applies to new subscribers. Current ones keep their price.',
      fields: {
        nameSv: 'Name (Swedish)',
        nameEn: 'Name (English)',
        descriptionSv: 'Description (Swedish)',
        descriptionEn: 'Description (English)',
        price: 'Price per month, kr excl. VAT',
        proposals: 'Proposals per month',
        trialDays: 'Trial days',
        position: 'Order',
        active: 'Shown to new companies',
      },
      saved: 'The plan is saved.',
      invalid: 'Check the fields.',
    },
    overview: {
      title: 'Overview',
      description: 'What is waiting for you today.',
      jobsToReview: 'Jobs to review',
      providersToVerify: 'Providers to verify',
      openReports: 'Open reports',
      openJobs: 'Open jobs',
      lastWeek: 'Last 7 days',
      latestActions: 'Latest admin actions',
      newJobs: 'New jobs',
      newUsers: 'New accounts',
      newProposals: 'Proposals sent',
      subscriptions: 'Active subscriptions',
      aiSpend: 'AI spend this month',
      aiBudget: 'Budget {budget}',
    },
    review: {
      details: 'Job details',
      title: 'Review jobs',
      description: 'New jobs stay hidden from providers until you approve them.',
      columns: {
        job: 'Job',
        category: 'Category',
        client: 'Client',
        location: 'Postal code',
        status: 'Status',
        received: 'Received',
      },
      searchPlaceholder: 'Search by title, client or email',
      filters: { all: 'All', in_review: 'To review', needs_info: 'Waiting for client' },
      empty: 'No jobs to review',
      emptyText: 'New jobs show up here.',
      approve: 'Approve',
      ask: 'Ask the client',
      reject: 'Reject',
      edit: 'Edit',
      editText: 'Fix spelling or make it clearer before approving. The change is logged.',
      approveTitle: 'Approve the job?',
      approveText: 'The job is published and matching providers get it right away.',
      approvedToast: 'The job is approved and sent to matching providers.',
      askTitle: 'Ask the client',
      askText: 'The client gets the questions by email and answers from their overview.',
      rejectTitle: 'Reject the job',
      rejectText: 'The client gets the reason by email.',
      question: 'Question {number}',
      addQuestion: 'Add a question',
      removeQuestion: 'Remove question {number}',
      reason: 'Reason',
      sendQuestion: 'Send the questions',
      askedToast: 'The questions are sent to the client.',
      rejectedToast: 'The job is rejected.',
      waitingForClient: "Waiting for the client's answer. You can still edit or reject the job.",
      reviewed: 'This job has already been reviewed.',
      rejectionReason: 'Reason for rejection',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      accountSuspended: 'The account is suspended',
      questions: 'Questions and answers',
      history: 'History',
      system: 'System',
      questionTooShort: 'Write at least 5 characters',
      reasonTooShort: 'Write at least 3 characters',
      conflict: 'Someone else changed the job. Reload the page and try again.',
      genericError: 'Could not save. Try again in a moment.',
    },
    providers: {
      noDocuments: 'No documents uploaded.',
      title: 'Providers',
      description: 'Verify new companies before they can send proposals.',
      searchPlaceholder: 'Search by company, org number, name or email',
      columns: {
        company: 'Company',
        orgNumber: 'Org number',
        contact: 'Contact',
        categories: 'Categories',
        counties: 'Counties',
        status: 'Status',
        submitted: 'Submitted',
      },
      noCompanyName: 'No company name yet',
      empty: 'No companies waiting',
      emptyText: 'Companies show up here once they have sent their profile.',
      verify: 'Verify',
      reject: 'Reject',
      verifyTitle: 'Verify the company?',
      verifyText:
        'Check the organisation number and F-skatt with Skatteverket before you verify. The company gets an email.',
      rejectTitle: 'Reject the company',
      rejectText: 'The provider gets the reason by email and can correct the profile and send it again.',
      reason: 'Reason',
      reasonTooShort: 'Write at least 3 characters',
      verifiedToast: 'The company is verified.',
      rejectedToast: 'The company is rejected.',
      company: 'Company',
      fSkatt: 'F-skatt',
      fSkattConfirmed: 'Yes, according to the provider',
      fSkattMissing: 'No',
      about: 'About the company',
      website: 'Website',
      services: 'Services and areas',
      categories: 'Categories',
      counties: 'Counties',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      openAccount: 'View account',
      accountSuspended: 'The account is suspended',
      submitted: 'Submitted',
      verifiedAt: 'Verified',
      rejectionReason: 'Reason for rejection',
      notSubmitted: 'The provider has not sent the profile yet.',
      decided: 'The profile has already been reviewed.',
      waitingAgain: 'The provider can correct the profile and send it again.',
      history: 'History',
      activity: 'Activity',
      system: 'System',
      conflict: 'Someone else changed the profile. Reload the page and try again.',
      genericError: 'Could not save. Try again in a moment.',
    },
    users: {
      title: 'Users',
      description: 'Every account on the platform.',
      searchPlaceholder: 'Search by name or email',
      columns: {
        name: 'Name',
        email: 'Email',
        roles: 'Roles',
        status: 'Status',
        lastLogin: 'Last login',
      },
      allRoles: 'All roles',
      allStatuses: 'All',
      neverLoggedIn: 'Never',
      empty: 'No accounts yet',
      account: 'Account',
      phone: 'Phone',
      language: 'Language',
      languages: { sv: 'Swedish', en: 'English' },
      created: 'Created',
      password: 'Password',
      passwordSet: 'Chosen',
      passwordNotSet: 'Not chosen yet',
      sessions: 'Logged-in devices',
      jobs: 'Jobs sent',
      permissions: 'Permissions',
      permissionNames: { finance: 'Finance', moderation: 'Moderation', support: 'Support' },
      company: 'Company',
      openProvider: 'View company',
      suspend: 'Suspend account',
      reactivate: 'Reactivate account',
      suspendTitle: 'Suspend the account?',
      suspendText:
        'The person is logged out at once and cannot log in until the account is reactivated. The reason is kept in the log and not shown to them.',
      reason: 'Reason',
      reasonTooShort: 'Write at least 3 characters',
      reactivateTitle: 'Reactivate the account?',
      reactivateText: 'The person can log in again.',
      note: 'Note (optional)',
      suspendedToast: 'The account is suspended.',
      reactivatedToast: 'The account is active again.',
      contact: 'Contact',
      ownAccount: 'This is your own account.',
      suspendedNotice: 'The account is suspended and cannot log in.',
      activity: 'Activity',
      conflict: 'Someone else changed the account. Reload the page and try again.',
      genericError: 'Could not save. Try again in a moment.',
    },
    audit: {
      title: 'Audit log',
      description: 'Who changed what, and when.',
      searchPlaceholder: 'Search by person or action, e.g. provider.',
      columns: { when: 'When', who: 'Who', what: 'What' },
      filters: { all: 'All', user: 'Accounts', provider: 'Providers', job: 'Jobs', report: 'Reports' },
      system: 'System',
      empty: 'No activity yet',
      open: 'Open',
      actions: {
        'user.create_from_job': 'Account created from the job form',
        'user.create_provider_signup': 'Account created as a provider',
        'user.password_changed': 'Changed the password',
        'user.password_set': 'Chose a password',
        'user.password_reset': 'Reset the password from a link',
        'user.profile_updated': 'Changed their details',
        'user.suspend': 'Suspended the account',
        'user.reactivate': 'Reactivated the account',
        'provider.start': 'Started as a provider',
        'provider.verify': 'Verified the company',
        'provider.reject': 'Rejected the company',
        'provider.profile_updated': 'Changed the company description',
        'provider.services_updated': 'Changed services and areas',
        'job.edit': 'Edited the job',
        'report.resolve': 'Resolved a report',
        'report.dismiss': 'Dismissed a report',
      } as Record<string, string>,
    },
    reports: {
      title: 'Reports',
      description: 'Reported messages, proposals, companies and people.',
      empty: 'No reports here',
      emptyText: 'Reports from clients and providers show up here.',
      columns: {
        what: 'What',
        reason: 'Reason',
        summary: 'Reported',
        subject: 'About',
        reported: 'Reported',
        status: 'Status',
      },
      targets: { message: 'Message', proposal: 'Proposal', provider: 'Company', user: 'Person' },
      statuses: { open: 'Open', resolved: 'Resolved', dismissed: 'Dismissed' },
      reporter: 'Reported by',
      deleted: 'Deleted account',
      otherReports: 'Other reports about them',
      reported: 'What was reported',
      reporterText: 'What the reporter wrote',
      hidden: 'Hidden',
      blocked: 'Conversation blocked',
      decision: 'Decision',
      chooseAction: 'What do you want to do?',
      actions: {
        warned: 'Warn',
        message_hidden: 'Hide the message',
        conversation_blocked: 'Block the conversation',
        account_suspended: 'Suspend the account',
      },
      actionTexts: {
        warned: 'They get a reminder of the rules by notification and email, without your note.',
        message_hidden: 'Neither side sees the text or files any more.',
        conversation_blocked: 'Both can read it, nobody can write.',
        account_suspended: 'Logged out at once and cannot log in. The note is the suspension reason.',
      },
      dismiss: 'Dismiss',
      dismissText: 'Nothing wrong; close the report without an action.',
      confirm: '{action}',
      note: 'Note',
      noteOptional: 'Note (optional)',
      noteHint: 'Kept with the report and in the audit log. Not shown to anyone else.',
      noteRequired: 'Write at least 3 characters',
      action: 'Action',
      handledBy: 'Handled by',
      handledAt: 'Handled',
      resolvedToast: 'Done. The report is resolved.',
      dismissedToast: 'The report is dismissed.',
      conflict: 'Someone else has already handled this report. Reload the page.',
      failed: 'Could not save. Try again in a moment.',
    },
  },
  messages: {
    title: 'Messages',
    description: 'A conversation opens when a client accepts a proposal.',
    empty: 'No conversations yet',
    emptyText: 'When a proposal is accepted, you can write to each other here.',
    pick: 'Pick a conversation',
    placeholder: 'Write a message',
    send: 'Send',
    attach: 'Add photos or PDFs',
    open: 'Open chat',
    noMessages: 'No messages yet',
    files: '{count} file(s)',
    you: 'You: {text}',
    unread: '{count} unread',
    older: 'Show older messages',
    start: 'Say hello and agree on the details.',
    hidden: 'This message was removed by Hemmafixa.',
    closed: 'The job is closed. You can read the conversation but not write.',
    blocked: 'Hemmafixa has stopped this conversation.',
    sendFailed: 'Could not send. Check your connection and try again.',
    fileFailed: '{name} could not be uploaded',
  },
  files: {
    title: 'Photos and documents',
    hint: 'JPG, PNG or WebP up to 10 MB, PDF up to 15 MB. At most {max} files.',
    drop: 'Drag photos or documents here',
    choose: 'Choose files',
    uploading: 'Uploading …',
    preparing: 'Preparing the photo …',
    failed: 'Upload failed',
    wrongFile: 'The file is too large or of the wrong type',
    remove: 'Remove {name}',
    removed: 'The file is removed.',
    removeFailed: 'Could not remove the file.',
    typeNotAllowed: '{name}: this file type cannot be uploaded.',
    tooLarge: '{name}: larger than {limit}.',
    tooMany: 'You can add at most {max} files.',
    none: 'No photos or documents.',
    locked: 'The photos and documents cannot change once the job is approved.',
  },
  notifications: {
    title: 'Notifications',
    description: 'What has happened with your jobs and proposals.',
    newJobMatch: 'New job in your area',
    newProposal: 'New proposal from {provider}',
    proposalAccepted: '{client} accepted your proposal',
    newMessage: 'New message from {sender}',
    proposalChosen: '{client} chose you for the job',
    jobClosedOther: 'The client chose another provider',
    jobClosedCancelled: 'The client cancelled the job',
    moderationWarning: 'A reminder about our rules',
    moderationWarningText:
      'We received a report about your account. Please follow the rules so everyone feels safe.',
    trialEnding: 'Your trial ends on {date}',
    trialEndingText: 'Then your card is charged for the plan each month.',
    paymentFailed: 'Your subscription payment failed',
    paymentFailedText: 'Update your card before {date} to keep sending proposals.',
    accessPaused: 'You cannot send proposals right now',
    accessPausedText: 'Your subscription is unpaid. Your conversations carry on as usual.',
    jobExpiring: 'Your job closes soon',
    jobNoProposals: 'No proposals yet on your job',
    markAllRead: 'Mark all as read',
    viewAll: 'View all notifications',
    empty: 'No notifications yet',
    emptyText: 'Things show up here when something happens, such as a new proposal or a new job.',
    noUnread: 'No unread notifications',
    unread: 'unread',
    show: 'Show',
    all: 'All',
    unreadFilter: 'Unread',
    settingsTitle: 'Notifications',
    settingsText:
      'You always see notifications here in the app. Choose whether you also want them by email and text.',
    email: 'Email',
    sms: 'Text message (SMS)',
    smsNeedsPhone: 'Add a phone number in your profile to get text messages.',
    savedToast: 'Saved.',
    saveFailed: 'Could not save. Try again in a moment.',
  },
  settings: {
    title: 'Settings',
    description: 'Your account details, notifications and password.',
    profile: 'Profile',
    name: 'Name',
    nameInvalid: 'Enter your name (2–100 characters).',
    phone: 'Phone',
    phoneHint: 'Optional.',
    phoneInvalid: 'Enter a valid phone number, e.g. 070-123 45 67.',
    email: 'Email',
    emailHint: 'We send login links and notifications here. Contact us if you need to change it.',
    savedToast: 'Your profile is saved.',
    password: 'Password',
    passwordText: 'Other devices are logged out when you change your password. This device stays logged in.',
    currentPassword: 'Current password',
    wrongCurrentPassword: 'The current password is not right.',
    changePassword: 'Change password',
    passwordChanged: 'Your password is changed. Other devices have been logged out.',
  },
  system: {
    title: 'System',
    description: 'Status of the API and the services it uses.',
    status: 'System status',
    versions: 'API version {api} · web version {web}',
    up: 'Running',
    down: 'Not reachable',
    unreachable: 'The API is not reachable',
    unreachableText:
      'The web app could not reach {url}. Start the API, or check the address in VITE_API_URL.',
    services: {
      database: 'Database',
      redis: 'Queues and rate limits',
      storage: 'File storage',
    },
  },
};
